import jp from "jsonpath"
import { WA_EPOCH_DATE_ISO, getNextDayInISODate, isWithinDateRange } from "./date-util"

export function getStaySharpStatsForAllStudents(staysharpData: IStaySharp[], tonpData, dateStart: Date|string, dateEnd?: Date|string): IStaySharpStats[] {
    staysharpData.sort((a, b) => a.emailAddress.localeCompare(b.emailAddress))
    let stats = staysharpData.map((studentData) => {
        let { emailAddress, firstName, lastName, summaries } = studentData
        let selectedDataStaysharp = getStudentStaySharpSummaries(studentData, dateStart, dateEnd)

        if (selectedDataStaysharp.length == 0) {
            let defaultStats: IStaySharpStats = {
                emailAddress,
                firstName,
                lastName,
                topicsStarted: -1,
                topicsCompleted: -1,
                questionsAnswered: -1,
                completedAccuracyEnglish: -1,
                completedAccuracyMath: -1,
                completedAccuracyTotal: -1,
                hintsOnIncorrect: -1,
                timeOnStaySharp: -1,
            }
            return defaultStats
        }

        let selectedDataTonP = getTonPData(tonpData, emailAddress, dateStart, dateEnd)
        return findStaysharpStats(studentData, selectedDataStaysharp, selectedDataTonP)
    })
    return stats
}

//--- Relevant types ---
export interface IStaySharp {
    emailAddress: string
    firstName: string
    lastName: string
    summaries: IStaySharpSummary[]
}

export interface IStaySharpSummary {
    topicId: string
    topicName: string
    completedQuestions: number
    totalQuestions: number
    status: string
    subject: string
    lessonId: string
    nofHintsUsed: number
    nofMistakes: number
    createdAt: string
    updatedAt: string
    completedAt: string
    completionProgressPercent: number
    numberOfCorrectQuestionsAnswered: number
    topicsCompletedPercentAccuracy: number
    topicsTotalPercentAccuracy: number
    percentHintsOnIncorrectQuestions: number
}

export interface IStaySharpStats {
    emailAddress: string
    firstName: string
    lastName: string
    topicsStarted: number
    topicsCompleted: number
    questionsAnswered: number
    completedAccuracyEnglish: number
    completedAccuracyMath: number
    completedAccuracyTotal: number
    hintsOnIncorrect: number
    timeOnStaySharp: number
}


//--- Helpers ---

function findStaysharpStats(student, dataStaysharp: IStaySharp[], dataTonP: any[]): IStaySharpStats {
    let { emailAddress, firstName, lastName } = student
    let summaries = dataStaysharp
    let topicsStarted = dataStaysharp.length
    let topicsCompleted = jp.query(summaries, "$..[?(@.completionProgressPercent==100)]").length
    let questionsAnswered = jp.query(dataStaysharp, "$..completedQuestions").reduce((acc, item) => acc + item, 0)
    let nofHintsOnIncorrect = jp.query(dataStaysharp, "$..nofHintsUsed").reduce((acc, item) => acc + item, 0)
    let nofCompletedQuestions = jp.query(dataStaysharp, "$..completedQuestions").reduce((acc, item) => acc + item, 0)
    let nofCorrectQuestions = jp.query(dataStaysharp, "$..numberOfCorrectQuestionsAnswered").reduce((acc, item) => acc + item, 0)
    let categoricalItemsE = jp.query(dataStaysharp, "$..[?(@.subject=='English')]")
    let categoricalItemsM = jp.query(dataStaysharp, "$..[?(@.subject=='Math')]")
    let nofIncorrectQuestions = nofCompletedQuestions - nofCorrectQuestions

    // Accuracies
    let nofCompltedQuestionsE = jp.query(categoricalItemsE, "$..completedQuestions").reduce((acc, item) => acc + item, 0)
    let nofCompltedQuestionsM = jp.query(categoricalItemsM, "$..completedQuestions").reduce((acc, item) => acc + item, 0)
    let nofCorrectQuestionsAnsweredE = jp.query(categoricalItemsE, "$..numberOfCorrectQuestionsAnswered").reduce((acc, item) => acc + item, 0)
    let nofCorrectQuestionsAnsweredM = jp.query(categoricalItemsM, "$..numberOfCorrectQuestionsAnswered").reduce((acc, item) => acc + item, 0)
    let completedAccuracyEnglish = nofCompltedQuestionsE > 0 ? nofCorrectQuestionsAnsweredE / nofCompltedQuestionsE : -1
    let completedAccuracyMath = nofCompltedQuestionsM > 0 ? nofCorrectQuestionsAnsweredM / nofCompltedQuestionsM : -1
    let completedAccuracyTotal = nofCompletedQuestions > 0 ? nofCorrectQuestions / nofCompletedQuestions : -1

    // TonP Info
    let staysharpStats = jp.query(dataTonP, "$..staysharps.stats")
    let timeOnStaySharp = jp.query(staysharpStats, "$..timeStudied").reduce((acc, item) => acc + item, 0)

    // Hints on incorrect (%)
    let hintsOnIncorrect = nofIncorrectQuestions > 0 ? nofHintsOnIncorrect/nofIncorrectQuestions: -1

    
    // StaySharp stats for the student
    let stats: IStaySharpStats = {
        emailAddress,
        firstName,
        lastName,
        topicsStarted,
        topicsCompleted,
        questionsAnswered,
        completedAccuracyEnglish,
        completedAccuracyMath,
        completedAccuracyTotal,
        hintsOnIncorrect,
        timeOnStaySharp,
    }

    return stats
}

function getStudentStaySharpSummaries(studentStaySharpData, startDate: Date|string, endDate?: Date|string) {
    let filteredData = studentStaySharpData.summaries.filter((summary) => {
        if (summary.createdAt == "") {
            // Old record detected!
            let epochDate = WA_EPOCH_DATE_ISO
            summary.createdAt = epochDate
        }
        endDate = endDate ? endDate : getNextDayInISODate()
        return isWithinDateRange(summary.createdAt, startDate, endDate)
    })

    return filteredData
}

function getTonPData(tonpData, studentEmail: string, startDate: Date|string, endDate?: Date|string) {
    let studentTonP = tonpData.filter((student) => student.email === studentEmail)
    let filteredTonP = studentTonP.filter((data) => {
        endDate = endDate ? endDate : getNextDayInISODate()
        return isWithinDateRange(data.date, startDate, endDate)
    })

    return filteredTonP
}